import * as styles from "./Window.css";

import { clsx } from "clsx";
import { type JSX, useEffect, useId, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

import { useFloatingPortalContainer } from "@frontend/contexts";
import {
  ShortcutScopeContext,
  useShortcutScope,
} from "@frontend/hooks/useScopedShortcut";
import { zIndex } from "@frontend/styles/tokens";
import { mergeRefs } from "@frontend/utils/mergeRefs";

import { WindowContext } from "./Window.context";
import type { WindowProps } from "./Window.types";
import {
  WINDOW_BLUR_VAR,
  WINDOW_DEFAULT_SIZE,
  WINDOW_EDGES,
  WINDOW_MIN_SIZE,
  WINDOW_OPACITY_VAR,
} from "./constants";
import { useWindowGeometry } from "./useWindowGeometry";
import { useWindowDepth } from "./useWindowStack";

export function Window({
  open,
  onOpenChange,
  onRectChange,
  title,
  anchorRef,
  defaultPosition,
  defaultSize = WINDOW_DEFAULT_SIZE,
  minSize = WINDOW_MIN_SIZE,
  maxSize,
  contain = true,
  opacity = 1,
  blur = 0,
  resizable = true,
  maximizable = true,
  dismissible = true,
  className,
  style,
  children,
  ref,
  ...props
}: WindowProps): JSX.Element | null {
  const titleId = useId();
  const frameRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  const portalContainer = useFloatingPortalContainer() ?? document.body;

  const { depth, raise } = useWindowDepth(titleId, open);

  const { scope, ref: scopeRef } = useShortcutScope({ modal: false });

  const {
    rect,
    interaction,
    maximized,
    startMove,
    startResize,
    toggleMaximize,
  } = useWindowGeometry({
    open,
    anchorRef,
    defaultPosition,
    defaultSize,
    minSize,
    maxSize,
    contain,
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    const frame = frameRef.current;
    const root = frame?.getRootNode() as Document | ShadowRoot | undefined;
    const active = root?.activeElement;

    restoreRef.current = active instanceof HTMLElement ? active : null;
    frame?.focus({ preventScroll: true });

    return () => restoreRef.current?.focus({ preventScroll: true });
  }, [open]);

  const rectChangeRef = useRef(onRectChange);

  useEffect(() => {
    rectChangeRef.current = onRectChange;
  });

  useEffect(() => {
    if (open && rect && null === interaction && !maximized) {
      rectChangeRef.current?.(rect);
    }
  }, [open, rect, interaction, maximized]);

  const context = useMemo(
    () => ({
      titleId,
      interaction,
      maximized,
      maximizable,
      dismissible,
      startMove,
      toggleMaximize,
      close: () => onOpenChange?.(false),
    }),
    [
      titleId,
      interaction,
      maximized,
      maximizable,
      dismissible,
      startMove,
      toggleMaximize,
      onOpenChange,
    ],
  );

  if (!open || !rect) {
    return null;
  }

  return createPortal(
    <WindowContext.Provider value={context}>
      <div
        data-slot="window"
        data-interaction={interaction ?? undefined}
        data-maximized={maximized || undefined}
        ref={mergeRefs(frameRef, scopeRef, ref)}
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        aria-label={title}
        tabIndex={-1}
        className={clsx(styles.frame, className)}
        style={
          {
            [WINDOW_OPACITY_VAR]: opacity,
            [WINDOW_BLUR_VAR]: blur > 0 ? `blur(${blur}px)` : undefined,
            zIndex: zIndex.window + depth,
            left: `${rect.x}px`,
            top: `${rect.y}px`,
            width: `${rect.width}px`,
            height: `${rect.height}px`,
            ...style,
          } as React.CSSProperties
        }
        onPointerDownCapture={() => {
          const frame = frameRef.current;
          const root = frame?.getRootNode() as
            Document | ShadowRoot | undefined;

          raise();

          if (frame && root && !frame.contains(root.activeElement)) {
            frame.focus({ preventScroll: true });
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape" && dismissible) {
            event.stopPropagation();
            onOpenChange?.(false);
          }
        }}
        {...props}
      >
        <ShortcutScopeContext value={scope}>{children}</ShortcutScopeContext>

        {resizable &&
          !maximized &&
          WINDOW_EDGES.map((edge) => (
            <div
              key={edge}
              data-slot="window-resize-handle"
              data-edge={edge}
              className={clsx(
                styles.resizeHandle,
                styles.resizeEdges[edge],
                edge === "se" && styles.resizeGrip,
              )}
              onPointerDown={(event) => startResize(event, edge)}
              aria-hidden="true"
            />
          ))}
      </div>
    </WindowContext.Provider>,
    portalContainer,
  );
}
